import type { AiriCard } from '../types/airiCard'

import { characterVoiceModelId } from '@proj-airi/stage-shared/character-voice'

import { Emotion } from '../constants/emotions'
import { velisCharacterCards } from './velis-character-cards'
import {
  velisAtriCardId,
  velisAtriDisplayModelId,
  velisNatsumeCardId,
  velisNatsumeDisplayModelId,
  velisPackagedDisplayModels,
} from './velis-packaged-display-models'

export {
  velisAtriCardId,
  velisAtriDisplayModelId,
  velisNatsumeCardId,
  velisNatsumeDisplayModelId,
  velisPackagedDisplayModels,
}

/** Natsume and ATRI models stay on the desktop. The web list keeps the demo card. */
export function cardAvailableOnWeb(id: string) {
  return id !== velisNatsumeCardId && id !== velisAtriCardId
}

/**
 * Expression file stems from the community rlmlr model whose names state an emotion.
 * Letter-named files stay unmapped until a developer labels them.
 * `加载中` is not `think`.
 * Upstream `rlmlr.model3.json` lists only the Idle motion `循环动画`.
 * `registerRlmlrExpressions` adds these five files before the zip is imported.
 */
export const natsumeLive2dExpressionNames: Partial<Record<Emotion, string>> = {
  [Emotion.Happy]: '激动',
  [Emotion.Sad]: '大哭',
  [Emotion.Angry]: '怒',
  [Emotion.Surprise]: '懵逼',
  [Emotion.Awkward]: '脸红',
}

/**
 * Stage fit places the model center on the bottom edge and draws it at twice the stage size.
 * This close-up then shows only the top of the head.
 * Scale 0.5 fits the model in the window. Y 50 moves the center to the middle.
 */
export const natsumeLive2dView = { x: 0, y: 50, scale: 0.5 }

export function natsumeLive2dExpression(emotion: string): string | undefined {
  if (!Object.hasOwn(natsumeLive2dExpressionNames, emotion))
    return undefined
  return natsumeLive2dExpressionNames[emotion as Emotion]
}

function createSpokenAiriCard(id: 'natsume' | 'atri', voiceId: string, appearance?: AiriCard['extensions']['airi']['appearance']): AiriCard {
  const source = velisCharacterCards[id]
  return {
    ...source,
    extensions: {
      ...source.extensions,
      airi: {
        modules: {
          consciousness: { provider: '', model: '' },
          vision: { provider: '', model: '' },
          speech: {
            provider: 'character-voice',
            model: characterVoiceModelId,
            voice_id: voiceId,
          },
        },
        agents: {},
        ...(appearance ? { appearance } : {}),
      },
    },
  }
}

export function createNatsumeAiriCard(): AiriCard {
  return createSpokenAiriCard('natsume', 'natsume-casual', {
    live2dModel: 'rlmlr',
    live2dExpressions: { ...natsumeLive2dExpressionNames },
    live2dView: natsumeLive2dView,
  })
}

/** ATRI's public model3.json has no expression files, so this card does not invent a map. */
export function createAtriAiriCard(): AiriCard {
  return createSpokenAiriCard('atri', 'atri', {
    live2dModel: 'ATRI',
  })
}
